import { useEffect, useState, type ReactElement } from "react";
import {
  Button,
  ConfigProvider,
  Drawer,
  Flex,
  Form,
  Input,
  List,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { targets, targetsLabels } from "../../constants";
import dragIcon from "../../../../../src/assets/images/drag-icon.svg";
import { EditButton } from "../../../../shared/layout/EditButton";

import { DeleteButton } from "../../../../shared/layout/DeleteButton";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DraggableStateSnapshot,
  type DraggableProvided,
  type DroppableProvided,
} from "@hello-pangea/dnd";
import { useSearchParams } from "react-router-dom";
import AxiosInstance from "../../../../shared/utils/axios";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { useCreateProtocolForm } from "../..";
import { DropdownEmptyState } from "../../../../shared/layout/DropdownEmptyState";
import { useProtocolsRouter } from "../../../../shared/custom-hooks/useProtocolsRouter";

interface objectiveObjectType {
  name: string;
  description: string;
  targets: string[];
  notes?: string;
}
const { TextArea } = Input;
const { Option } = Select;

const ObjectivesComponent = (): ReactElement => {
  const { t } = useTranslation();
  const { form, setFormValidationData } = useCreateProtocolForm();
  const current = form.getFieldValue("objectives") || [];

  if (!current) {
    form.setFieldValue("objectives", []);
  }

  const [newObjectiveForm] = Form.useForm<objectiveObjectType>();

  const [objectivesData, setObjectivesData] =
    useState<objectiveObjectType[]>(current);

  useEffect(() => {
    setObjectivesData(current);
  }, [form.getFieldValue("objectives")]);
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get("protocol_id");
  const { workspaceId, orgId } = useProtocolsRouter();
  const [existingData, setExistingData] = useState<any>(null);
  const getProtocolData = async (): Promise<any> => {
    try {
      const response = await AxiosInstance.get(
        `/protocol/${protocolId as string}`,
        {
          headers: {
            "org-id": orgId ?? "",
            "workspace-id": workspaceId ?? "",
          },
        }
      );
      setExistingData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (protocolId) {
      getProtocolData().catch((error) => {
        console.error("Error fetching data:", error);
      });
    }
  }, [protocolId]);

  useEffect(() => {
    if (existingData) {
      form.setFieldsValue({
        objectives: existingData.objectives,
      });
      // setTrialInfo(trialData);
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        objectives: existingData.objectives,
      }));
    }
  }, [existingData]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditingIndex, setIsEditingIndex] = useState<number | null>(null);
  const showDrawer = (): void => {
    setIsDrawerOpen(true);
  };

  const closeDrawer = (): void => {
    setIsEditingIndex(null);
    setIsDrawerOpen(false);
    clearObjectivesForm();
  };

  const clearObjectivesForm = (): void => {
    newObjectiveForm.resetFields();
  };

  const handleCancel = (): void => {
    closeDrawer();
  };

  const handleAddObjective = (): void => {
    const newObjective = {
      name: newObjectiveForm.getFieldValue("name"),
      description: newObjectiveForm.getFieldValue("description"),
      targets: newObjectiveForm.getFieldValue("targets"),
      notes: "",
    };
    if (!form.getFieldsValue(true).objectives) {
      form.setFieldsValue({ objectives: [] });
    }
    if (newObjective.name === undefined || newObjective.name === "") {
      console.error("objective name cannot be empty");
      return;
    }
    const currentObjectives = form.getFieldValue("objectives") || [];

    if (isEditingIndex !== null) {
      // Editing an existing objective

      currentObjectives[isEditingIndex] = newObjective;
    } else {
      // Adding a new objective

      currentObjectives.push(newObjective);

      form.setFieldsValue({
        objectives: currentObjectives,
      });
    }

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      objectives: currentObjectives,
    }));
    closeDrawer();
  };

  const handleObjectiveDelete = (indexToDelete: number): void => {
    const currentList = current;

    const deleted = currentList.toSpliced(indexToDelete, 1);
    form.setFieldsValue({ objectives: deleted });
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      objectives: deleted,
    }));
    setExpandedDescriptions({});
  };

  const handelObjectiveEdit = (indexToEdit: number): void => {
    const currentList = current;
    const itemToEdit = currentList[indexToEdit];
    newObjectiveForm.setFieldsValue({
      name: itemToEdit.name,
      description: itemToEdit.description,
      targets: itemToEdit.targets,
    });
    setIsEditingIndex(indexToEdit);
    showDrawer();
  };

  function handleOnDragEnd(result: any): void {
    if (!result.destination) return;

    const clonedListItems = JSON.parse(JSON.stringify(objectivesData));
    const [reorderedItem] = clonedListItems.splice(result.source.index, 1);

    clonedListItems.splice(result.destination.index, 0, reorderedItem);

    form.setFieldsValue({
      objectives: clonedListItems,
    });
    setObjectivesData(clonedListItems);
    setExpandedDescriptions({});
  }

  const [submittable, setSubmittable] = useState<boolean>(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<string, boolean>
  >({});

  // Watch all values
  const values = Form.useWatch([], newObjectiveForm);

  const validate = (): void => {
    newObjectiveForm
      .validateFields({ validateOnly: true })
      .then((x) => {
        setSubmittable(true);
      })
      .catch((y) => {
        setSubmittable(false);
      });
  };

  useEffect(() => {
    validate();
  }, [newObjectiveForm, values]);

  const getSafeTranslatedText = (key: string, fallback: string): string => {
    const translatedValue = t(key);

    if (typeof translatedValue === "string") {
      return fallback;
    }

    return formatTranslation(translatedValue) || fallback;
  };

  const toggleDescription = (objectiveKey: string): void => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [objectiveKey]: !prev[objectiveKey],
    }));
  };

  const renderObjectiveItem = (
    item: objectiveObjectType,
    index: number,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ): ReactElement => {
    const objectiveKey = item?.name || `objective-${index}`;
    const description = item?.description?.trim() || "";
    const targetValues = item?.targets ?? [];
    const shouldTruncate = description.length > 120;
    const isExpanded = expandedDescriptions[objectiveKey] || false;
    const seeMoreText = getSafeTranslatedText(
      "createProtocol.objectives.seeMore",
      "See more"
    );
    const seeLessText = getSafeTranslatedText(
      "createProtocol.objectives.seeLess",
      "See less"
    );
    const seeMoreAriaLabel = getSafeTranslatedText(
      "createProtocol.objectives.seeMoreAriaLabel",
      "Expand objective description"
    );
    const seeLessAriaLabel = getSafeTranslatedText(
      "createProtocol.objectives.seeLessAriaLabel",
      "Collapse objective description"
    );
    const displayedDescription =
      shouldTruncate && !isExpanded
        ? `${description.slice(0, 120).trimEnd()}...`
        : description;

    return (
      <div ref={provided.innerRef} {...provided.draggableProps}>
        <List.Item
          className={`objective-list-item ${
            snapshot.isDragging ? "objective-item-dragging" : ""
          }`}
        >
          <div className="objective-item-main">
            <div className="objective-item-content">
              <div className="objective-item-header">
                <div className="objective-item-title">
                  <div className="objective-item-icon">
                    <img
                      src={dragIcon}
                      alt="dragIcon"
                      {...provided.dragHandleProps}
                    />
                  </div>
                  <div className="objective-item-name">{item?.name}</div>
                </div>
              </div>

              <div className="objective-item-details">
                {targetValues.length > 0 ? (
                  <div className="objective-item-target">
                    <span className="objective-item-label">
                      {formatTranslation(
                        t("createProtocol.objectives.objectiveItemTarget")
                      )}
                    </span>
                    <div className="objective-item-target-values">
                      {targetValues.map((target) => (
                        <span
                          key={`${item?.name}-${target}`}
                          className="objective-item-target-chip"
                        >
                          {formatTranslation(
                            t(targetsLabels[target] || target)
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {description && (
                  <div className="objective-item-description">
                    <span className="objective-item-label">
                      {formatTranslation(
                        t("createProtocol.objectives.objectiveItemDescription")
                      )}
                    </span>
                    <div
                      className={`objective-item-description-text ${
                        isExpanded ? "expanded" : ""
                      }`}
                    >
                      {displayedDescription}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="objective-item-actions">
              <div className="objective-item-actions-top">
                <Flex vertical={false} align="center" gap={8}>
                  <EditButton
                    from="objectives"
                    onClick={() => {
                      handelObjectiveEdit(index);
                    }}
                  />
                  <DeleteButton
                    from="objectives"
                    onClick={() => {
                      handleObjectiveDelete(index);
                    }}
                    dataName={item.name}
                  />
                </Flex>
              </div>
              {shouldTruncate && (
                <button
                  type="button"
                  className="objective-item-description-toggle"
                  aria-label={isExpanded ? seeLessAriaLabel : seeMoreAriaLabel}
                  onClick={() => {
                    toggleDescription(objectiveKey);
                  }}
                >
                  {isExpanded ? seeLessText : seeMoreText}
                </button>
              )}
            </div>
          </div>
        </List.Item>
      </div>
    );
  };

  const renderDroppable = (provided: DroppableProvided): ReactElement => (
    <div ref={provided.innerRef} {...provided.droppableProps}>
      <List
        locale={{ emptyText: <ListEmptyText showDrawer={showDrawer} /> }}
        className={"list-container "}
        size="large"
        dataSource={objectivesData ?? []}
        rowKey="name"
        renderItem={(item, index) => (
          <Draggable
            draggableId={`draggable-${index}`}
            index={index}
            key={`draggable-${index}`}
          >
            {(provided, snapshot) =>
              renderObjectiveItem(item, index, provided, snapshot)
            }
          </Draggable>
        )}
      />
      {provided.placeholder}
    </div>
  );

  return (
    <div className="objectives-component" data-testid="objectives.component">
      {/*  className : ant-drawer-body */}
      <Drawer
        className="objective-drawer"
        rootClassName="objective-drawer"
        data-testid="objectives.objectiveDrawerTitle"
        title={formatTranslation(
          t("createProtocol.objectives.objectiveDrawerTitle")
        )}
        onClose={closeDrawer}
        open={isDrawerOpen}
      >
        <Form
          form={newObjectiveForm}
          name="objectives-form"
          initialValues={{ name: "", description: "", targets: [] }}
          layout="vertical"
          // Form state management happens here
          onValuesChange={(eventValue: any) => {
            const [fieldName, fieldValue] = Object.entries(eventValue)[0]; // [0] access is okay as only 1 object will be processed by event
            newObjectiveForm.setFieldValue(
              fieldName as "name" | "description" | "targets" | "notes",
              fieldValue
            );
          }}
        >
          <div className="basicInfo-form-section">
            <Form.Item
              name="name"
              key="name"
              label={formatTranslation(
                t("createProtocol.objectives.objectiveName")
              )}
              rules={[
                { required: true },
                {
                  // Mark validator as async to satisfy ESLint
                  async validator(_, value) {
                    if (value && value.length > 100) {
                      return await Promise.reject(
                        formatTranslation(
                          t(
                            "createProtocol.objectives.objectiveDescription.maxWordsError"
                          )
                        )
                      );
                    }
                    // No need for a return statement here
                  },
                },
              ]}
            >
              <Input
                data-testid="objectives.objectiveName"
                maxLength={101}
                placeholder={formatTranslation(
                  t("createProtocol.objectives.objectiveName.placeholder")
                )}
              ></Input>
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label={formatTranslation(
              t("createProtocol.objectives.objectiveDescription")
            )}
            rules={[
              {
                // Mark validator as async to satisfy ESLint
                async validator(_, value) {
                  if (value && value.length > 500) {
                    return await Promise.reject(
                      formatTranslation(
                        t(
                          "createProtocol.objectives.objectiveDescription.maxWordsError"
                        )
                      )
                    );
                  }
                  // No need for a return statement here
                },
              },
            ]}
          >
            <TextArea
              rows={3}
              maxLength={501}
              placeholder={formatTranslation(
                t("createProtocol.objectives.objectiveDescription.placeholder")
              )}
            />
          </Form.Item>

          <ConfigProvider renderEmpty={() => <DropdownEmptyState />}>
            <Form.Item
              data-testid="objectiveTargets"
              name="targets"
              label={formatTranslation(
                t("createProtocol.objectives.objectiveTargets")
              )}
              rules={[
                {
                  required: true,
                  message: formatTranslation(
                    t("createProtocol.objectives.objectiveTargetsMessage")
                  ),
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                data-testid="objectives.objectiveTargets"
                placeholder={formatTranslation(
                  t("createProtocol.objectives.targetPlaceholder")
                )}
                // optionRender={}  // write custom jsx for checkbox as per figma
              >
                {targets.map((target, index) => (
                  <Option key={index} value={target.value}>
                    {/* {target.label} */}
                    {formatTranslation(t(target.label))}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </ConfigProvider>
          <div className="objective-drawer-footer">
            <Button
              onClick={handleCancel}
              data-testid="objectives.cancelButton"
            >
              {formatTranslation(t("createProtocol.objectives.cancelButton"))}
            </Button>
            <Button
              onClick={handleAddObjective}
              className="objective-drawer-save"
              data-testid="objectives.saveButton"
              htmlType="submit"
              disabled={!submittable}
            >
              {formatTranslation(t("createProtocol.objectives.saveButton"))}
            </Button>
          </div>
        </Form>
      </Drawer>
      <div className="objectives-header">
        <div className="objectives-header-text">
          <h2>
            {formatTranslation(t("createProtocol.objectives.title"))}
            <span className="required-star">*</span>
          </h2>
          <p>{formatTranslation(t("createProtocol.objectives.description"))}</p>
        </div>
      </div>

      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="DropId">
          {(provided) => renderDroppable(provided)}
        </Droppable>
      </DragDropContext>

      {objectivesData && objectivesData.length > 0 && (
        <Flex
          className="ant-list-empty-text"
          justify="center"
          align="center"
          onClick={showDrawer}
        >
          <Button onClick={showDrawer} className="add-objective-btn">
            <PlusOutlined />
            <span>
              {formatTranslation(t("createProtocol.objectives.addButton"))}
            </span>
          </Button>
        </Flex>
      )}
    </div>
  );
};

const ListEmptyText = ({
  showDrawer,
}: {
  showDrawer: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      justify="center"
      gap={"24px"}
      className="objective-empty-state"
      onClick={showDrawer}
    >
      <Button onClick={showDrawer} className="add-objective-btn">
        <PlusOutlined />
        <span>
          {formatTranslation(t("createProtocol.objectives.addButton"))}
        </span>
      </Button>
    </Flex>
  );
};

export default ObjectivesComponent;
